import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { SettlementLineItemType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { SettlementLineItem } from '~hooks/useSettlementLineItems/models';
import useSettlementLineItems from '~hooks/useSettlementLineItems/useSettlementLineItems';
import { Settlement } from '~hooks/useSettlements/models';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import RebillSettlementDetailsRow from './RebillSettlementDetailsRow';
import VendorDriverAdjustmentRow, {
  convertSettlementLineItemToDTO,
} from './VendorDriverAdjustmentRow';

const vendorDriverAdjustmentValidationSchema = yup.object().shape({
  id: yup.string().required(),
  adjustPay: yup.boolean().required(),
  driver: yup
    .object()
    .shape({
      id: yup.string(),
      fullName: yup.string(),
    })
    .nullable()
    .notRequired(),
  vendor: yup
    .object()
    .shape({
      id: yup.string(),
      name: yup.string(),
    })
    .nullable()
    .notRequired(),
  adjustmentTotal: yup.number().required(),
  addOnDescription: yup.string().notRequired(),
});

const vendorDriverAdjustmentsFormValidationSchema = yup.object().shape({
  adjustments: yup
    .array()
    .of(vendorDriverAdjustmentValidationSchema)
    .required()
    .min(1, 'At least one adjustment is required'),
});

export type VendorDriverAdjustmentsFormData = yup.InferType<
  typeof vendorDriverAdjustmentsFormValidationSchema
>;

interface VendorDriverAdjustmentsFormProps {
  settlement?: Settlement;
}

export interface VendorDriverAdjustmentsFormRef {
  submit: () => Promise<VendorDriverAdjustmentsFormData | undefined>;
  getValues: () => VendorDriverAdjustmentsFormData;
  reset: () => void;
}

function LoadingPlaceholder() {
  return (
    <Box
      sx={{
        display: 'grid',
        justifyContent: 'center',
        alignContent: 'center',
        height: '100px',
        bgcolor: 'background.paper',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

const VendorDriverAdjustmentsForm = observer(
  forwardRef<VendorDriverAdjustmentsFormRef, VendorDriverAdjustmentsFormProps>(
    function VendorDriverAdjustmentsForm({ settlement }, ref) {
      const [lineItems, setLineItems] = useState<SettlementLineItem[]>([]);
      const { getAllSettlementLineItems, isLoading: isLoadingAllSettlementLineItems } =
        useSettlementLineItems();
      const { userStore } = useStores();
      const companyId = getEffectiveUserCompanyId(userStore);

      const methods = useForm<VendorDriverAdjustmentsFormData>({
        resolver: yupResolver(vendorDriverAdjustmentsFormValidationSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
          adjustments: [],
        },
      });

      const { handleSubmit, getValues, reset } = methods;

      const headerColumns = useMemo(
        () => [
          t(
            'approvals.driver_pay.settlements.rebill_adjustments.table_headers.adjust_pay',
          ),
          t('approvals.driver_pay.settlements.rebill_adjustments.table_headers.driver'),
          t('approvals.driver_pay.settlements.rebill_adjustments.table_headers.vendor'),
          t(
            'approvals.driver_pay.settlements.rebill_adjustments.table_headers.adjustment_total',
          ),
          t(
            'approvals.driver_pay.settlements.rebill_adjustments.table_headers.add_on_description',
          ),
        ],
        [],
      );

      const gridConfig = useMemo(
        () => ({
          display: 'grid',
          gridTemplateColumns: '80px repeat(5, 1fr)',
          gridTemplateRows: 'minmax(40px, auto)',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'background.paper',
          px: 1,
        }),
        [],
      );

      // Hydrate line items on mount
      useEffect(() => {
        if (!settlement?.id) return;

        const getSettlementLineItems = async () => {
          try {
            const lineItemsRequest = await getAllSettlementLineItems(
              companyId,
              String(settlement.id),
              SettlementLineItemType.LOAD,
            );
            const lineItems = lineItemsRequest.map((group) => {
              return group.lineItems;
            });
            setLineItems(lineItems.flat());
          } catch (error) {
            console.error('Failed to load settlement line items:', error);
          }
        };

        getSettlementLineItems();
      }, [settlement?.id]);

      // Create adjustment DTOs from line items and reset form
      const adjustmentDTOs = useMemo(() => {
        return lineItems
          .map((lineItem, index) => convertSettlementLineItemToDTO(lineItem, index))
          .filter((dto) => dto.adjustmentTotal !== 0);
      }, [lineItems]);

      // Reset form when adjustment DTOs change
      useEffect(() => {
        if (adjustmentDTOs.length > 0) {
          reset({ adjustments: adjustmentDTOs });
        }
      }, [adjustmentDTOs]);

      useImperativeHandle(
        ref,
        () => ({
          submit() {
            return new Promise((resolve) => {
              handleSubmit(
                (data) => {
                  resolve(data);
                },
                () => {
                  resolve(undefined);
                },
              )();
            });
          },
          getValues() {
            return getValues();
          },
          reset() {
            reset();
          },
        }),
        [handleSubmit, getValues, reset],
      );

      if (!settlement) {
        return (
          <Box sx={{ p: 2 }}>
            <Typography>{t('common.loading')}</Typography>
          </Box>
        );
      }

      return (
        <FormProvider {...methods}>
          <Box>
            <RebillSettlementDetailsRow settlement={settlement} />

            <Typography variant="h6" gutterBottom>
              {t(
                'approvals.driver_pay.settlements.rebill_adjustments.add_ons_section_title',
              )}
            </Typography>
            <Typography
              variant="body2"
              gutterBottom
              sx={{
                color: 'text.secondary',
              }}
            >
              {t(
                'approvals.driver_pay.settlements.rebill_adjustments.add_ons_description',
              )}
            </Typography>

            <Box sx={{ mt: 2 }}>
              {/* Table Header */}
              <Box
                sx={(theme) => ({ ...gridConfig, bgcolor: theme.semantic.surfaceSunken })}
              >
                {headerColumns.map((header, idx) => (
                  <Typography
                    key={header}
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      textAlign: idx === 0 ? 'center' : 'left',
                      gridColumn: idx === 4 ? '5 / 7' : undefined, // Last column spans 2 columns
                    }}
                  >
                    {header}
                  </Typography>
                ))}
              </Box>

              {/* Loading State */}
              {isLoadingAllSettlementLineItems ? (
                <LoadingPlaceholder />
              ) : adjustmentDTOs.length === 0 ? (
                /* No Adjustments Message */
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100px',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t(
                      'approvals.driver_pay.settlements.rebill_adjustments.no_adjustments_message',
                    )}
                  </Typography>
                </Box>
              ) : (
                /* Adjustment Rows */
                adjustmentDTOs.map((dto, index) => (
                  <VendorDriverAdjustmentRow
                    key={dto.id}
                    data={dto}
                    gridConfig={gridConfig}
                    index={index}
                  />
                ))
              )}
            </Box>
          </Box>
        </FormProvider>
      );
    },
  ),
);

export default VendorDriverAdjustmentsForm;
